// Authentication service - GraphQL integration
import graphqlClient, { setAuthToken, decodeJWTToken } from './graphqlClient';
import { environment } from '../environment/environment';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';

/**
 * Extract error message from backend response
 */
const extractBackendErrorMessage = (error: any): string | null => {
  // Check nested error response from Hasura actions
  if (error.extensions?.internal?.response?.body?.message) {
    return error.extensions.internal.response.body.message;
  }

  return null;
};

/**
 * Map backend errors to user-friendly messages
 */
const getErrorMessage = (error: any, fallbackMessage: string): string => {
  console.log('Error object:', error); // For debugging

  // GraphQL errors - Check these FIRST before generic network errors
  if (error.response?.errors?.[0]) {
    const gqlError = error.response.errors[0];

    // Try to extract the actual backend error message from nested response
    const backendMsg = extractBackendErrorMessage(gqlError);
    if (backendMsg) {
      // Return exact backend message - it's already user-friendly from backend
      return backendMsg;
    }

    // Fallback to other error fields
    if (gqlError.extensions?.internal?.response?.body?.detail) {
      const detail = gqlError.extensions.internal.response.body.detail;

      // Map common backend errors
      if (detail.includes('Invalid OTP') || detail.includes('invalid otp')) {
        return 'The verification code you entered is incorrect. Please try again.';
      }
      if (detail.includes('expired')) {
        return 'The verification code has expired. Please request a new one.';
      }
      if (detail.includes('User not registered') || detail.includes('not found') || detail.includes('not exist') || detail.includes('not registered')) {
        return 'User not registered.';
      }

      return detail;
    }

    // GraphQL error message
    if (gqlError.message) {
      if (gqlError.message.includes('not authorized')) {
        return 'You are not authorized. Please try again.';
      }
      if (gqlError.message.includes('validation')) {
        return 'Please enter a valid email address.';
      }
      if (
        gqlError.message.includes('internal') ||
        gqlError.message.includes('unexpected') ||
        gqlError.message.includes('500')
      ) {
        return 'Server error occurred. Please try again after a moment.';
      }
      return gqlError.message;
    }
  }

  // Network errors and connection failures - Check AFTER GraphQL errors
  if (error.message?.includes('fetch') || error.message?.includes('Network')) {
    return 'Network connection failed. Please check your internet and try again.';
  }

  if (error.message?.includes('timeout') || error.message?.includes('408')) {
    return 'Server is not responding. Please try again after a moment.';
  }

  // Connection to server errors (database, host errors, etc.)
  if (
    error.message?.includes('connection') ||
    error.message?.includes('FATAL') ||
    error.message?.includes('password authentication') ||
    error.message?.includes('host') ||
    error.message?.includes('port') ||
    error.message?.includes('Connection failure') ||
    error.message?.includes('http exception')
  ) {
    return 'Something went wrong. Please try again later.';
  }

  return fallbackMessage;
};

// Type definitions
export interface SendOtpResponse {
  message: string;
  email: string;
  user_id: string;
  otp_session_id: string;
  expires_at: string;
}

export interface VerifyOtpResponse {
  access_token: string;
  token_type: string;
}

export interface SendVerificationCodeResult {
  success: boolean;
  message: string;
  data?: {
    email: string;
    userId: string;
    otpSessionId: string;
    expiresAt: string;
  };
  error?: any;
}

export interface VerifyCodeResult {
  success: boolean;
  token?: string;
  tokenType?: string;
  user?: {
    email: string;
  };
  data?: {
    accessToken: string;
    tokenType: string;
  };
  message?: string;
  error?: any;
}

// GraphQL Query for fetching user data (uses graphqlClient)
const GET_USER_BY_EMAIL_QUERY = `
  query GetUserByEmail($email: String!) {
    app_user(where: { email: { _eq: $email } }) {
      id
      email
      name
      role
    }
  }
`;

/**
 * Send verification code to email using GraphQL
 */
export const sendVerificationCode = async (email: string): Promise<SendVerificationCodeResult> => {
  try {
    // Use raw fetch to avoid operationName issue with graphql-request
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation sendOtp($email: String!) {
            sendOtp(email: $email) {
              message
              email
              user_id
              otp_session_id
              expires_at
            }
          }
        `,
        variables: { email: email }
      })
    });

    const result = await response.json();

    if (result.errors) {
      // Throw error object with GraphQL response so getErrorMessage can parse it
      const error = new Error('OTP request failed');
      (error as any).response = result;
      throw error;
    }

    return {
      success: true,
      message: result.data.sendOtp.message || 'OTP sent successfully',
      data: {
        email: result.data.sendOtp.email,
        userId: result.data.sendOtp.user_id,
        otpSessionId: result.data.sendOtp.otp_session_id,
        expiresAt: result.data.sendOtp.expires_at,
      }
    };
  } catch (error: any) {
    console.error('Error sending OTP:', error);

    // Use getErrorMessage to extract the exact backend error
    const errorMessage = getErrorMessage(error, 'Failed to send verification code. Please try again after a moment.');

    return {
      success: false,
      message: errorMessage,
      error: error
    };
  }
};


/**
 * Fetch current user information
 * First tries to get actual user name from database, falls back to email-derived name
 */
export const fetchCurrentUser = async (token: string): Promise<any> => {
  try {
    // Extract basic user data from JWT token
    const jwtData = decodeJWTToken(token);
    if (!jwtData) {
      return null;
    }

    // Try to fetch actual user name from database
    try {
      const dbResponse = await graphqlClient.request<{ app_user: any[] }>(
        GET_USER_BY_EMAIL_QUERY,
        { email: jwtData.email }
      );

      if (dbResponse.app_user && dbResponse.app_user.length > 0) {
        const dbUser = dbResponse.app_user[0];
        // Use database data if available
        return {
          ...jwtData,
          id: dbUser.id || jwtData.id,
          name: dbUser.name || jwtData.name,
          role: dbUser.role || jwtData.role,
        };
      }
    } catch (dbError) {
      console.warn('Failed to fetch user from database, using JWT data:', dbError);
    }

    // Return JWT data if database query fails
    return jwtData;
  } catch (error: any) {
    console.error('Error fetching current user:', error);
    return null;
  }
};


/**
 * Verify code and login using GraphQL
 */
export const verifyCodeAndLogin = async (email: string, code: string): Promise<VerifyCodeResult> => {
  try {
    // Use raw fetch to avoid operationName issue with graphql-request
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation verifyOtp($email: String!, $otp: String!) {
            verifyOtp(email: $email, otp: $otp) {
              access_token
              token_type
            }
          }
        `,
        variables: { email: email, otp: code }
      })
    });

    const result = await response.json();

    if (result.errors) {
      // Throw error object with GraphQL response so getErrorMessage can parse it
      const error = new Error('OTP verification failed');
      (error as any).response = result;
      throw error;
    }

    const verifyOtpData = result.data.verifyOtp;

    // Store the access token in localStorage
    localStorage.setItem('access_token', verifyOtpData.access_token);
    localStorage.setItem('token_type', verifyOtpData.token_type);

    // Set the token in GraphQL client so we can fetch user data
    setAuthToken(verifyOtpData.access_token);

    // Desktop only: save token + register the MCP server in Claude's config.
    // No-op in the web build (Tauri's invoke is unavailable in a browser).
    if (isTauri()) {
      // Save token to ~/.atlas_mcp_token so the MCP server can use it
      invoke('save_mcp_token', { token: verifyOtpData.access_token }).catch(console.error);

      // Auto-register the MCP server in Claude's config (first login sets it up).
      // Pass the active Hasura URL so the server queries the same env we logged into.
      invoke('register_mcp_server', {
        hasuraUrl: environment.graphqlEndpoint,
        baseUrl: environment.skillsBaseUrl,
      })
        .then((msg) => console.log('MCP:', msg))
        .catch(console.error);
    }

    // Fetch user info with the new token
    const userInfo = await fetchCurrentUser(verifyOtpData.access_token);

    const finalUser = userInfo || {
      email: email,
      name: email.split('@')[0],
      role: 'user',
    };

    return {
      success: true,
      token: verifyOtpData.access_token,
      tokenType: verifyOtpData.token_type,
      user: finalUser,
      data: {
        accessToken: verifyOtpData.access_token,
        tokenType: verifyOtpData.token_type,
      }
    };
  } catch (error: any) {
    console.error('Error verifying OTP:', error);

    // Use the getErrorMessage function to extract and map the error
    const errorMessage = getErrorMessage(error, 'Failed to verify code. Please try again.');

    return {
      success: false,
      message: errorMessage,
      error: error
    };
  }
};

/**
 * Logout user - Clears all browser storage completely
 */
export const logout = async (): Promise<{ success: boolean }> => {
  return new Promise((resolve) => {
    // Remove authorization header from GraphQL client first
    setAuthToken('');

    // Clear MCP server token file (desktop only)
    if (isTauri()) {
      invoke('clear_mcp_token').catch(console.error);
    }

    // Clear all localStorage items (includes auth tokens, user data, etc.)
    localStorage.clear();

    // Clear all sessionStorage items
    sessionStorage.clear();

    // Clear IndexedDB if present
    if (window.indexedDB) {
      const dbNames = ['opportunity_db', 'auth_db'];
      dbNames.forEach((dbName) => {
        const deleteRequest = window.indexedDB.deleteDatabase(dbName);
        deleteRequest.onerror = () => {
          console.warn(`Failed to clear IndexedDB ${dbName}`);
        };
      });
    }

    setTimeout(() => {
      resolve({ success: true });
    }, 500);
  });
};
